/**
 * Bar-by-bar alignment view (W2-08 timing; DESIGN.md §4.15 "BAR BY BAR", §5.2.3; U§2.1 the osu!lazer timing
 * comparison): one strip per bar of the song, stacked, showing where the onsets fall inside the bar. With the right
 * tempo and first beat the beats form straight vertical columns on the beat lines; a slightly wrong tempo slants
 * them (the drift line under the toolbar says which way).
 *
 *  - Rows follow the meter (a 3/4 section has 3-beat strips; a bar cut short by a meter change is a short strip).
 *  - Hover listens (a short snippet of the song under the pointer while paused; setting `ext.timing.scrubOnHover`),
 *    click seeks, Alt+wheel nudges the tempo in effect at that bar by 0.01 BPM (a burst is one undo step), the
 *    wheel scrolls through the bars; with keyboard focus: Up / Down scroll, Page Up / Page Down by a page.
 *  - "Lock region" keeps a chosen range of bars (e.g. the intro) on screen; unlocked, the rows follow the
 *    playhead while playing.
 * The strips are computed on this thread from the cached onset envelope (core/audio-dsp/alignment.ts), so a tempo
 * nudge redraws them at once.
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import type { AppServices } from '../../app/registry/types';
import { alignmentRows, rowCentroids, stripeDrift } from '../../core/audio-dsp/alignment';
import type { OnsetEnvelope } from '../../core/audio-dsp/types';
import { formatSec } from '../../core/time/beatSyntax';
import { Icon, IconButton, Select } from '../../ui';
import { useCanvasView, vline, type CanvasFrame } from './canvasView';
import { m } from './timing.messages';
import { viewOf, type TimingView } from './timingDoc';
import { setPointBpm } from './timingEdits';
import { useSettingsValue, useTransport, type TimingText } from './timingHooks';
import { barAtMusicSec, barSpans, pointAtBeat, steppedBpm, type BarSpan } from './timingModel';
import { timingSessionFor, useTimingSession, type TimingSessionState } from './timingSession';
import s from './Timing.module.css';

/** Width of the bar-number column (CSS px). */
const LABEL_W = 56;
/** Length of a hover snippet (ms) and the least time between two. */
export const SCRUB_MS = 140;
/** Columns of the drift estimate, and the total drift (ms over the shown rows) below which the stripes count as
 * straight: about a hop of the onset envelope, the resolution of the strips. */
const DRIFT_COLS = 1024;
const DRIFT_STRAIGHT_MS = 12;
/** Most rows a locked region shows. */
const MAX_LOCKED_ROWS = 64;
export const ROW_CHOICES = ['8', '16', '32', '64'] as const;
/** Setting value of "Fit": as many rows as fit the view's height at about FIT_ROW_PX each (UX-082). */
export const FIT_ROWS = 0;
export const DEFAULT_ROWS = FIT_ROWS;
const FIT_ROW_PX = 28;

/** Rows shown for a setting (`FIT_ROWS`: from the canvas height, at least 4). */
export function effectiveRows(setting: number, heightPx: number): number {
  if (setting !== FIT_ROWS) return setting;
  if (!(heightPx > 0)) return 16;
  return Math.max(4, Math.min(64, Math.floor(heightPx / FIT_ROW_PX)));
}

export interface AlignmentViewProps {
  readonly services: AppServices;
  readonly text: TimingText;
  readonly view: TimingView;
  readonly onsets: OnsetEnvelope | null;
  readonly durationSec: number;
}

const selectAlignment = (st: TimingSessionState) => st.alignment;

/** Rows setting (ext.timing.alignmentRows): a whole number of bars from 4 to 64, or 0 (fit), else DEFAULT_ROWS. */
export function rowsSetting(ext: Readonly<Record<string, unknown>>): number {
  const v = ext['timing.alignmentRows'];
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : Number.NaN;
  return Number.isInteger(n) && ((n >= 4 && n <= 64) || n === FIT_ROWS) ? n : DEFAULT_ROWS;
}

/** Height of an element in CSS px, following resizes. */
function useHeight(ref: { readonly current: HTMLElement | null }): number {
  const [h, setH] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (el === null) return undefined;
    setH(el.getBoundingClientRect().height);
    if (typeof ResizeObserver !== 'function') return undefined;
    const ro = new ResizeObserver(() => {
      setH(el.getBoundingClientRect().height);
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
    };
  }, [ref]);
  return h;
}

/** Plays a short snippet at a chart time and returns the playhead where it was (hover scrubbing). */
export function playSnippet(services: Pick<AppServices, 'audio'>, chartSec: number): void {
  const tr = services.audio.transport;
  if (tr.state.playing) return;
  const back = tr.chartTime();
  void services.audio
    .ensureContext()
    .then(() => tr.play({ from: chartSec, mode: 'holdReturn' }))
    .then(() => {
      setTimeout(() => {
        if (tr.state.playing && tr.state.mode === 'holdReturn') {
          tr.stop();
          tr.seek(back);
        }
      }, SCRUB_MS);
    })
    .catch(() => undefined);
}

interface Hover {
  readonly row: number;
  readonly frac: number;
  readonly musicSec: number;
}

interface RowCache {
  readonly key: string;
  readonly rows: Float32Array;
  readonly cols: number;
}

export function AlignmentView(props: AlignmentViewProps) {
  const { services, text, view, onsets } = props;
  const settings = useSettingsValue(services);
  const transport = useTransport(services);
  const alignment = useTimingSession(services, selectAlignment);
  const rowsSettingValue = rowsSetting(settings.ext);
  const scrubOn = settings.ext['timing.scrubOnHover'] !== false;
  const [hover, setHover] = useState<Hover | null>(null);
  const hostRef = useRef<HTMLDivElement | null>(null);
  const rowsWanted = effectiveRows(rowsSettingValue, useHeight(hostRef));
  const cache = useRef<RowCache | null>(null);
  const lastScrub = useRef(0);

  const locked = alignment.locked;
  const firstBar = locked?.from ?? alignment.firstBar;
  const count = locked === null ? rowsWanted : Math.min(MAX_LOCKED_ROWS, locked.to - locked.from + 1);
  const spans = useMemo(
    () => barSpans(view.tempo, view.meterMap, view.shiftSec, firstBar, count),
    [view.tempo, view.meterMap, view.shiftSec, firstBar, count],
  );

  const rowsFor = useCallback(
    (cols: number): RowCache | null => {
      if (onsets === null || spans.length === 0) return null;
      const key = `${String(cols)}|${spans.map((b) => `${String(b.startSec)}:${String(b.lenSec)}`).join(',')}`;
      if (cache.current?.key === key && cache.current.cols === cols) return cache.current;
      const rows = alignmentRows(
        onsets,
        spans.map((b) => b.startSec),
        spans.map((b) => b.lenSec),
        cols,
      );
      cache.current = { key, rows, cols };
      return cache.current;
    },
    [onsets, spans],
  );

  const playheadMusic = useCallback((): number => services.audio.transport.musicTime(), [services]);

  const draw = useCallback(
    (f: CanvasFrame) => {
      drawAlignment(f, {
        spans,
        data: rowsFor(Math.max(16, Math.min(2048, Math.floor(f.width - LABEL_W)))),
        hover,
        playhead: playheadMusic(),
        barLabel: (bar) => text.t(m.alignmentBar, { bar }),
      });
    },
    [spans, rowsFor, hover, text, playheadMusic],
  );
  const { canvasRef } = useCanvasView(services.scheduler, 'timing.alignment', draw, transport.playing);

  // Rows follow the playhead while playing (unless a region is locked).
  const { tempo, meterMap, shiftSec } = view;
  useEffect(() => {
    if (!transport.playing || locked !== null) return undefined;
    const id = 'timing.alignment.follow';
    const off = services.scheduler.add(id, 'idle', () => {
      const session = timingSessionFor(services);
      const st = session.get().alignment;
      if (st.locked !== null) return;
      const bar = barAtMusicSec(tempo, meterMap, shiftSec, playheadMusic());
      if (bar < st.firstBar || bar >= st.firstBar + rowsWanted) {
        session.set({ alignment: { ...st, firstBar: Math.max(1, bar - 1) } });
      }
      services.scheduler.request(id);
    });
    services.scheduler.request(id);
    return off;
  }, [transport.playing, locked, services, tempo, meterMap, shiftSec, rowsWanted, playheadMusic]);

  const setFirstBar = useCallback(
    (bar: number): void => {
      const session = timingSessionFor(services);
      const st = session.get().alignment;
      if (st.locked !== null) return;
      session.set({ alignment: { ...st, firstBar: Math.max(1, Math.round(bar)) } });
    },
    [services],
  );

  const hoverAt = (clientX: number, clientY: number): Hover | null => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (rect === undefined || spans.length === 0) return null;
    const x = clientX - rect.left - LABEL_W;
    const w = rect.width - LABEL_W;
    if (x < 0 || w <= 0) return null;
    const rowH = rect.height / spans.length;
    const row = Math.min(spans.length - 1, Math.max(0, Math.floor((clientY - rect.top) / rowH)));
    const span = spans[row];
    if (span === undefined) return null;
    const frac = Math.max(0, Math.min(1, x / w));
    return { row, frac, musicSec: span.startSec + frac * span.lenSec };
  };

  // Alt+wheel nudges the tempo, the wheel scrolls bars (native listener: React wheel handlers are passive).
  const wheelState = useRef({ spans, firstBar, view, hoverAt });
  useLayoutEffect(() => {
    wheelState.current = { spans, firstBar, view, hoverAt };
  });
  useEffect(() => {
    const el = hostRef.current;
    if (el === null) return undefined;
    const onWheel = (e: WheelEvent): void => {
      if (e.deltaY === 0 || e.ctrlKey || e.metaKey) return;
      const st = wheelState.current;
      e.preventDefault();
      if (e.altKey) {
        const h = st.hoverAt(e.clientX, e.clientY);
        const span = h === null ? st.spans[0] : st.spans[h.row];
        // The current tempo (several wheel steps can arrive before a re-render).
        const points = services.store === null ? st.view.points : viewOf(services.store).points;
        const point = span === undefined ? points[0] : pointAtBeat(points, span.startBeat);
        if (point === undefined || point === null) return;
        const next = steppedBpm(point.bpm, e.deltaY < 0 ? 1 : -1, 0.01);
        if (next !== null) setPointBpm(services, point.id, next, 'timing.alignWheel');
        return;
      }
      setFirstBar(st.firstBar + (e.deltaY > 0 ? 1 : -1) * Math.max(1, Math.round(Math.abs(e.deltaY) / 100)));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      el.removeEventListener('wheel', onWheel);
    };
  }, [services, setFirstBar]);

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>): void => {
    if (e.ctrlKey || e.metaKey || e.altKey || locked !== null) return;
    const page = Math.max(1, count - 1);
    const moves: Readonly<Record<string, number>> = { ArrowUp: -1, ArrowDown: 1, PageUp: -page, PageDown: page };
    const d = moves[e.key];
    if (d === undefined) return;
    e.preventDefault();
    e.stopPropagation();
    setFirstBar(firstBar + d);
  };

  // Drift of the stripes (columns per bar -> ms per bar).
  // Total drift of the stripes over the rows that have onsets (ms): straight below DRIFT_STRAIGHT_MS.
  const driftMs = useMemo(() => {
    if (onsets === null || spans.length < 3) return null;
    const rows = alignmentRows(
      onsets,
      spans.map((b) => b.startSec),
      spans.map((b) => b.lenSec),
      DRIFT_COLS,
    );
    const perBar = Math.max(1, Math.round(spans[0]?.lenBeats ?? 4));
    const withData = rowCentroids(rows, DRIFT_COLS, perBar).filter((x) => Number.isFinite(x)).length;
    if (withData < 3) return null;
    const d = stripeDrift(rows, DRIFT_COLS, perBar);
    const len = spans[0]?.lenSec ?? 0;
    return (d / DRIFT_COLS) * len * 1000 * (withData - 1);
  }, [onsets, spans]);
  const straight = driftMs !== null && Math.abs(driftMs) < DRIFT_STRAIGHT_MS;

  const toggleLock = (): void => {
    const session = timingSessionFor(services);
    const st = session.get().alignment;
    session.set({
      alignment:
        st.locked === null
          ? { ...st, locked: { from: firstBar, to: firstBar + count - 1 } }
          : { firstBar: st.locked.from, locked: null },
    });
  };

  const hasData = onsets !== null && spans.length > 0;
  return (
    <div className={s.canvasBox} style={{ borderBottom: 'none' }}>
      <div className={s.toolbar}>
        <span className={s.toolbarTitle}>{text.t(m.alignmentTitle)}</span>
        <IconButton
          icon="ChevronUp"
          size="sm"
          label={{ key: m.alignmentPrev }}
          disabledReason={
            locked !== null ? { key: m.alignmentLocked, params: { from: locked.from, to: locked.to } } : null
          }
          onClick={() => {
            setFirstBar(firstBar - Math.max(1, count - 1));
          }}
        />
        <IconButton
          icon="ChevronDown"
          size="sm"
          label={{ key: m.alignmentNext }}
          disabledReason={
            locked !== null ? { key: m.alignmentLocked, params: { from: locked.from, to: locked.to } } : null
          }
          onClick={() => {
            setFirstBar(firstBar + Math.max(1, count - 1));
          }}
        />
        <Select<string>
          ariaLabel={text.t(m.alignmentRows)}
          value={String(rowsSettingValue)}
          options={[...new Set([String(FIT_ROWS), ...ROW_CHOICES, String(rowsSettingValue)])]
            .sort((a, b) => Number(a) - Number(b))
            .map((v) => ({ value: v, label: v === String(FIT_ROWS) ? text.t(m.alignmentRowsFit) : v }))}
          onChange={(v) => {
            services.settings.setExt('timing.alignmentRows', Number(v));
          }}
        />
        <span className={s.subtle}>{text.t(m.alignmentRows)}</span>
        <IconButton
          icon={locked === null ? 'LockOpen' : 'Lock'}
          size="sm"
          pressed={locked !== null}
          label={{ key: locked === null ? m.alignmentLock : m.alignmentUnlock }}
          onClick={toggleLock}
        />
        <span className={s.subtle}>
          {locked !== null
            ? text.t(m.alignmentLocked, { from: locked.from, to: locked.to })
            : text.t(m.alignmentFollow)}
        </span>
        <IconButton
          icon="Ear"
          size="sm"
          pressed={scrubOn}
          label={{ key: m.alignmentScrub }}
          onClick={() => {
            services.settings.setExt('timing.scrubOnHover', !scrubOn);
          }}
        />
        <span className={`${s.grow} ${s.subtle}`}>{text.t(m.alignmentHelp)}</span>
      </div>
      {driftMs !== null && (
        <div className={s.status} data-kind={straight ? 'ok' : 'warning'} role="status">
          <Icon name={straight ? 'CircleCheck' : 'TriangleAlert'} size={14} />
          <span>
            {text.t(straight ? m.alignmentStraight : driftMs < 0 ? m.alignmentDriftLeft : m.alignmentDriftRight)}
          </span>
        </div>
      )}
      <div
        ref={hostRef}
        className={s.canvasHost}
        tabIndex={0}
        role="group"
        aria-label={text.t(m.alignmentLabel)}
        data-wheel="native"
        data-testid="timing-alignment"
        onKeyDown={onKeyDown}
      >
        <canvas
          ref={canvasRef}
          className={s.canvas}
          data-cursor="crosshair"
          onPointerMove={(e) => {
            const h = hoverAt(e.clientX, e.clientY);
            setHover(h);
            if (h === null || !scrubOn) return;
            const now = performance.now();
            if (now - lastScrub.current < SCRUB_MS) return;
            lastScrub.current = now;
            playSnippet(services, h.musicSec - view.shiftSec);
          }}
          onPointerLeave={() => {
            setHover(null);
          }}
          onClick={(e) => {
            const h = hoverAt(e.clientX, e.clientY);
            if (h !== null) services.audio.transport.seek(h.musicSec - view.shiftSec);
          }}
        />
        {hover !== null && (
          <span className={s.readout}>
            {text.t(m.alignmentReadout, { bar: spans[hover.row]?.bar ?? 0, time: formatSec(hover.musicSec) })}
          </span>
        )}
        {!hasData && (
          <div className={s.overlayMessage}>
            <span>{text.t(m.alignmentNoData)}</span>
          </div>
        )}
      </div>
    </div>
  );
}

interface AlignmentDraw {
  readonly spans: readonly BarSpan[];
  readonly data: RowCache | null;
  readonly hover: Hover | null;
  readonly playhead: number;
  barLabel(bar: number): string;
}

function drawAlignment(f: CanvasFrame, d: AlignmentDraw): void {
  const { ctx, width: w, height: h, theme } = f;
  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = theme.background;
  ctx.fillRect(0, 0, w, h);
  ctx.fillStyle = theme.gutterBackground;
  ctx.fillRect(0, 0, LABEL_W, h);
  const n = d.spans.length;
  if (n === 0) return;
  const rowH = h / n;
  const stripW = w - LABEL_W;
  ctx.font = `11px ${theme.fontMono}`;
  ctx.textBaseline = 'middle';
  for (let r = 0; r < n; r++) {
    const span = d.spans[r];
    if (span === undefined) continue;
    const y = r * rowH;
    ctx.fillStyle = theme.textMuted;
    if (rowH >= 10) ctx.fillText(d.barLabel(span.bar), 4, y + rowH / 2);
    // Onset strip.
    const data = d.data;
    if (data !== null) {
      const cols = data.cols;
      const cw = stripW / cols;
      ctx.fillStyle = theme.accent;
      for (let k = 0; k < cols; k++) {
        const v = data.rows[r * cols + k] ?? 0;
        if (v <= 0.02) continue;
        ctx.globalAlpha = Math.min(1, Math.sqrt(v));
        ctx.fillRect(LABEL_W + k * cw, y + 1, Math.max(1, cw), Math.max(1, rowH - 2));
      }
      ctx.globalAlpha = 1;
    }
    // Beat lines (the bar line at the start is the strip edge).
    ctx.strokeStyle = theme.gridBeat;
    const beats = Math.floor(span.lenBeats + 1e-9);
    for (let k = 1; k <= beats; k++) {
      const x = LABEL_W + (k / span.lenBeats) * stripW;
      if (x < w - 1) vline(ctx, x, y, y + rowH);
    }
    ctx.strokeStyle = theme.divider;
    ctx.beginPath();
    ctx.moveTo(0, Math.round(y + rowH) - 0.5);
    ctx.lineTo(w, Math.round(y + rowH) - 0.5);
    ctx.stroke();
    // Playhead in its bar.
    if (d.playhead >= span.startSec && d.playhead < span.startSec + span.lenSec) {
      ctx.strokeStyle = theme.playhead;
      vline(ctx, LABEL_W + ((d.playhead - span.startSec) / span.lenSec) * stripW, y, y + rowH, 2);
    }
  }
  ctx.strokeStyle = theme.gridBar;
  vline(ctx, LABEL_W, 0, h);
  if (d.hover !== null) {
    ctx.strokeStyle = theme.text;
    ctx.setLineDash([2, 2]);
    vline(ctx, LABEL_W + d.hover.frac * stripW, 0, h);
    ctx.setLineDash([]);
  }
}
